import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin';
import { exportService } from '@/services/export';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { format } from 'date-fns';
import { FileText, Download, Loader2, AlertCircle } from 'lucide-react';

export default function ReportGenerator() {
    const { data: classes } = useQuery({ queryKey: ['classes'], queryFn: adminService.listClasses });
    
    const [selectedClass, setSelectedClass] = useState('');
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [isGenerated, setIsGenerated] = useState(false);

    const { data: reportData, isLoading, error } = useQuery({
        queryKey: ['admin-monthly-overview', year, month, selectedClass],
        queryFn: () => adminService.getMonthlyAttendanceOverview({ 
            year, 
            month, 
            classId: selectedClass || undefined 
        }),
        enabled: isGenerated,
    });

    const handleGenerate = () => {
        if (!selectedClass) {
            alert('Please select a class first.');
            return;
        }
        setIsGenerated(true);
    };

    const handleDownloadPDF = () => {
        if (!reportData) return;

        const className = classes?.find((c: any) => c.id === selectedClass)?.name || 'Class';
        const monthName = format(new Date(year, month - 1), 'MMMM');

        const headers = ['Date', 'Present', 'Absent', 'Late', 'Leave', 'Percentage'];
        const rows = reportData.map((day: any) => [
            day.date,
            day.present,
            day.absent,
            day.late,
            day.leave,
            `${day.percentage}%`
        ]);

        // Calculate summary
        const totalPresent = reportData.reduce((acc: number, curr: any) => acc + curr.present, 0);
        const totalAbsent = reportData.reduce((acc: number, curr: any) => acc + curr.absent, 0);
        const totalLate = reportData.reduce((acc: number, curr: any) => acc + curr.late, 0);
        const avgPercentage = (reportData.reduce((acc: number, curr: any) => acc + curr.percentage, 0) / reportData.length).toFixed(1);

        exportService.generatePDF({
            title: `Attendance Report - ${monthName} ${year}`,
            subtitle: `Class: ${className}`,
            headers,
            rows,
            summary: [
                { label: 'Avg. Attendance', value: `${avgPercentage}%` },
                { label: 'Total Present', value: totalPresent },
                { label: 'Total Absent', value: totalAbsent },
                { label: 'Total Late', value: totalLate },
            ]
        });
    };

    return (
        <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                    <FileText size={20} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Generate Attendance Report</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Class</label>
                    <select
                        className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        value={selectedClass}
                        onChange={(e) => {
                            setSelectedClass(e.target.value);
                            setIsGenerated(false);
                        }}
                    >
                        <option value="">Select Class</option>
                        {classes?.map((cls: any) => (
                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                    <select
                        className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        value={year}
                        onChange={(e) => {
                            setYear(Number(e.target.value));
                            setIsGenerated(false);
                        }}
                    >
                        {[2024, 2025, 2026].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Month</label>
                    <select
                        className="w-full rounded-lg border-slate-200 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        value={month}
                        onChange={(e) => {
                            setMonth(Number(e.target.value));
                            setIsGenerated(false);
                        }}
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{format(new Date(2000, m - 1), 'MMMM')}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-end">
                    <Button 
                        onClick={handleGenerate} 
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-[38px]"
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader2 className="animate-spin" size={18} /> : 'Preview Report'}
                    </Button>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
                    <AlertCircle size={18} />
                    <p className="text-sm">Could not load this report. Check the class and month, then try Preview again.</p>
                </div>
            )}

            {isGenerated && reportData && (
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mt-6 animate-in fade-in slide-in-from-top-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">
                                Attendance Report - {format(new Date(year, month - 1), 'MMMM')} {year}
                            </h4>
                            <p className="text-sm text-slate-500">
                                Class: <span className="font-medium text-slate-700">{classes?.find((c: any) => c.id === selectedClass)?.name}</span>
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button 
                                onClick={handleDownloadPDF}
                                variant="secondary" 
                                className="gap-2 border-slate-300 text-slate-700 hover:bg-white hover:shadow-sm"
                            >
                                <Download size={18} />
                                <span>Download PDF</span>
                            </Button>
                            <Button
                                type="button"
                                disabled
                                title="Sending to parents is not set up yet"
                                className="bg-slate-200 text-slate-600 gap-2 shadow-sm cursor-not-allowed"
                            >
                                <span>Send to Parents — not set up yet</span>
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                        <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                            <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Avg. Attendance</span>
                            <span className="text-2xl font-black text-slate-900">
                                {(reportData.reduce((acc: number, curr: any) => acc + curr.percentage, 0) / reportData.length).toFixed(1)}%
                            </span>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                            <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Present Days</span>
                            <span className="text-2xl font-black text-green-600">
                                {reportData.reduce((acc: number, curr: any) => acc + curr.present, 0)}
                            </span>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                            <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Late Arrivals</span>
                            <span className="text-2xl font-black text-amber-500">
                                {reportData.reduce((acc: number, curr: any) => acc + curr.late, 0)}
                            </span>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                            <span className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Absences</span>
                            <span className="text-2xl font-black text-rose-500">
                                {reportData.reduce((acc: number, curr: any) => acc + curr.absent, 0)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}
